import { FileTextOutlined, InboxOutlined } from "@ant-design/icons";
import { useGlobalAction } from "@gadgetinc/react";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { message, Upload } from "antd";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../../api";
import { Button } from "../../../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../externalComponents/tailwind/dialog";
import { Field, Label } from "../../../../externalComponents/tailwind/fieldset";
import { Input } from "../../../../externalComponents/tailwind/input";
import { Text } from "../../../../externalComponents/tailwind/text";

const { Dragger } = Upload;

const validateCategory = (value) => {
  return /^[a-zA-Z0-9_]+$/.test(value);
};

export default function ImportCouponDialog({
  open,
  onClose,
  onSuccess,
  categories,
}) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [validationError, setValidationError] = useState("");
  const containerRef = useRef(null);

  const [{ fetching: isUploading }, importCoupons] = useGlobalAction(
    api.settings.importCouponCodesController,
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (category) {
      const filtered = categories.filter((cat) =>
        cat.toLowerCase().includes(category.toLowerCase()),
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [category, categories]);

  const handleCategoryChange = (value) => {
    const lowercaseValue = value?.toLowerCase();

    if (!validateCategory(lowercaseValue)) {
      setValidationError(
        "Only lowercase letters (a-z), numbers (0-9), and underscores (_) are allowed",
      );
      return;
    }

    setValidationError("");
    setCategory(lowercaseValue);
    setShowDropdown(true);
  };

  const handleCategorySelect = (selectedCategory) => {
    setCategory(selectedCategory);
    setShowDropdown(false);
    setValidationError("");
  };

  const handleImport = async () => {
    if (!file) {
      message.error("Please select a CSV file");
      return;
    }

    if (!category) {
      message.error("Please enter a category");
      return;
    }

    if (!validateCategory(category)) {
      message.error(
        "Category contains invalid characters. Only a-z, 0-9, and _ are allowed",
      );
      return;
    }

    try {
      const text = await file.text();
      const lines = text.split(/\r\n|\n/).filter((line) => line.trim());
      const codes = lines.map((line) => line.trim()).filter(Boolean);

      if (codes.length === 0) {
        message.error("No valid codes found in the file");
        return;
      }

      await importCoupons({ codes, category });

      setFile(null);
      setCategory("");
      message.info("Import in progress, it may take a few minutes to process.");

      onSuccess(category);
    } catch (error) {
      message.error("Failed to import coupon codes: " + error.message);
    }
  };

  const handleClose = () => {
    setFile(null);
    setCategory("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle>Import coupon codes</DialogTitle>
      <DialogBody>
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <Text className="text-sm text-yellow-800">
            <strong>Important:</strong> Use the same category for all codes of
            the same group. All codes from the CSV file will be imported with
            the category specified below.
          </Text>
        </div>

        <Field className="mb-4">
          <Label>Category</Label>
          <div ref={containerRef} className="relative">
            <Input
              autoComplete="off"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              placeholder="Enter or select category (e.g., promo, samsung_plus)"
              className={validationError ? "border-red-500" : ""}
            />
            {validationError && (
              <Text className="text-xs text-red-600 mt-1">
                {validationError}
              </Text>
            )}
            {showDropdown && filteredCategories.length > 0 && (
              <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredCategories.map((cat) => (
                  <li
                    key={cat}
                    onClick={() => handleCategorySelect(cat)}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 hover:text-gray-900"
                  >
                    {cat}
                  </li>
                ))}
                {!categories.includes(category) &&
                  category &&
                  validateCategory(category) && (
                    <li
                      onClick={() => handleCategorySelect(category)}
                      className="px-4 py-2 cursor-pointer hover:bg-blue-100 hover:text-blue-900 border-t border-gray-200 bg-blue-50"
                    >
                      <strong>Create new:</strong> {category}
                    </li>
                  )}
              </ul>
            )}
          </div>
          <Text className="text-xs text-gray-500 mt-1">
            Only lowercase letters (a-z), numbers (0-9), and underscores (_)
            allowed
          </Text>
        </Field>

        <Field className="h-[20vh] min-h-[150px] max-h-[200px]">
          <Label>CSV file (one code per line)</Label>
          <Dragger
            name="file"
            showUploadList={false}
            maxCount={1}
            accept="text/csv,.csv"
            disabled={isUploading}
            multiple={false}
            customRequest={({ file, onSuccess }) => {
              setFile(file);
              onSuccess();
            }}
            beforeUpload={(uploadFile) => {
              const isCSV =
                uploadFile.type === "application/vnd.ms-excel" ||
                uploadFile.type === "text/csv" ||
                uploadFile.name.endsWith(".csv");

              if (!isCSV) {
                message.error("You can only import CSV files");
                return false;
              }
              return true;
            }}
          >
            <p className="ant-upload-drag-icon">
              {file ? <FileTextOutlined /> : <InboxOutlined />}
            </p>
            <p className="ant-upload-text">
              Click or drag-and-drop a file to import
            </p>
            {file ? (
              <p className="ant-upload-hint">{file.name}</p>
            ) : (
              <p className="ant-upload-hint">
                Upload a .csv file with one coupon code per line
              </p>
            )}
          </Dragger>
        </Field>
      </DialogBody>
      <DialogActions>
        <Button
          color="secondaryButton"
          onClick={handleClose}
          disabled={isUploading}
        >
          Cancel
        </Button>
        <Button
          color="primaryButton"
          onClick={handleImport}
          loading={isUploading}
          disabled={!file || !category}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
